<template>
    <div class="talk-right-pane">
        <el-tabs v-if="showTab" v-model="selectedTabName" class="talk-right-pane-tabs" type="border-card" @tab-click="handleChangeTab" :before-leave="beforeLeave">
            <el-tab-pane :label="$t('message.title_talkList')" name="talkRecord">
                <talkrecord-list
                    v-if="selectedTabName === 'talkRecord'"
                    v-bind:appData="localAppData"
                    v-bind:date="localDate"
                    v-bind:startTime="localStartTime"
                    v-bind:endTime="localEndTime"
                    v-bind:languageIndex="localLanguageIndex"
                    v-bind:selected="localSelected"
                    v-bind:isTalkgroup="localIsTalkgroup">
                </talkrecord-list>
            </el-tab-pane>
            <el-tab-pane v-if="hasBuddycomCall" :label="$t('message.title_talkList_call')" name="BuddycomCallRecord">
                <buddycom-call-talk-record-list
                    v-if="selectedTabName === 'BuddycomCallRecord'"
                    v-bind:appData="localAppData"
                    v-bind:date="localDate"
                    v-bind:startTime="localStartTime"
                    v-bind:endTime="localEndTime"
                    v-bind:languageIndex="localLanguageIndex"
                    v-bind:selected="localSelected"
                    v-bind:isTalkgroup="localIsTalkgroup">
                </buddycom-call-talk-record-list>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script>
import commonUtil from '../commonutil.js';
import TalkrecordList from "./TalkrecordList.vue";
import BuddycomCallTalkRecordList from "../buddycomcall/BuddycomCallTalkRecordList.vue";
import InitialTalkRightPane from "./InitialTalkRightPane.vue";

export default {
    components: {
        TalkrecordList,
        BuddycomCallTalkRecordList,
        InitialTalkRightPane
    },
    props: {
        selectedNode: { type: Object, default: null },
        appData: { type: Object, required: true },
        date: { type: Date, default: () => new Date() },
        startTime: { type: String, default: '00:00' },
        endTime: { type: String, default: '24:00' },
        languageIndex: { type: Number, default: 0 },
        selected: { type: Array, default: () => [] },
        selectedItemType: { type: String, default: '' },
        isTalkgroup: { type: Boolean, default: true }
    },
    data() {
        return {
            // Default to talkRecord tab; list components show their own initial notice when nothing is selected
            selectedTabName: 'talkRecord',
            // Local cached copies of props to survive tab switches or transient parent updates
            localSelectedNode: null,
            localAppData: {},
            localDate: new Date(),
            localStartTime: '00:00',
            localEndTime: '24:00',
            localLanguageIndex: 0,
            localSelected: [],
            localSelectedItemType: '',
            localIsTalkgroup: false,
            // Local copies for BuddyComCall feature flags to ensure reactive updates on nested changes
            localEnableBuddyComCall: false,
            localEnableBuddyComCallTrial: false
        };
    },
    computed: {
        showTab() {
            // Always show tabs; initial content guides the user before selection
            return true;
        },
        hasBuddycomCall() {
            // Call tab appears only when enabled and target is talkgroup
            return (this.localEnableBuddyComCall || this.localEnableBuddyComCallTrial) && !!this.localIsTalkgroup;
        }
    },
    created() {
        // Initialize local cache from initial props
        this.syncFromProps({
            selectedNode: this.selectedNode,
            appData: this.appData,
            date: this.date,
            startTime: this.startTime,
            endTime: this.endTime,
            languageIndex: this.languageIndex,
            selected: this.selected,
            selectedItemType: this.selectedItemType,
            isTalkgroup: this.isTalkgroup
        });
        // Initialize local feature flags from appData
        if (this.appData) {
            this.localEnableBuddyComCall = !!this.appData.enableBuddyComCall;
            this.localEnableBuddyComCallTrial = !!this.appData.enableBuddyComCallTrial;
        }
    },
    watch: {
        selectedNode(newVal) { if (newVal !== undefined && newVal !== null) this.localSelectedNode = newVal; },
        appData(newVal) {
            if (newVal !== undefined && newVal !== null) {
                this.localAppData = newVal;
                // sync feature flags when appData reference changes
                this.localEnableBuddyComCall = !!newVal.enableBuddyComCall;
                this.localEnableBuddyComCallTrial = !!newVal.enableBuddyComCallTrial;
            }
        },
        date(newVal) { if (newVal) this.localDate = new Date(newVal); },
        startTime(newVal) { if (newVal !== undefined && newVal !== null && newVal !== '') this.localStartTime = newVal; },
        endTime(newVal) { if (newVal !== undefined && newVal !== null && newVal !== '') this.localEndTime = newVal; },
        languageIndex(newVal) { if (newVal !== undefined && newVal !== null) this.localLanguageIndex = newVal; },
        selected(newVal) {if (Array.isArray(newVal)) this.localSelected = newVal.slice();},
        selectedItemType(newVal) {
            if (newVal !== undefined && newVal !== null && newVal !== '') this.localSelectedItemType = newVal;
            if (newVal === commonUtil.getMessageText(this.$i18n,"item_user")) {
                this.selectedTabName = 'talkRecord';
            }
        },
        isTalkgroup(newVal) {if (typeof newVal === 'boolean') this.localIsTalkgroup = newVal;},
        'appData.enableBuddyComCall'(val) { this.localEnableBuddyComCall = !!val; },
        'appData.enableBuddyComCallTrial'(val) { this.localEnableBuddyComCallTrial = !!val; }
    },
    methods: {
        getMessageText(messageKey) {
            return commonUtil.getMessageText(this.$i18n, messageKey);
        },
        handleChangeTab(tab) {
            // Element UI passes TabPane instance; sync selectedTabName with its name
            if (tab && tab.name) this.selectedTabName = tab.name;
        },
        beforeLeave() {
            return true;
        },
        syncFromProps(p) {
            if (!p) p = {};
            if (p.selectedNode !== undefined && p.selectedNode !== null) this.localSelectedNode = p.selectedNode;
            if (p.appData !== undefined && p.appData !== null) this.localAppData = p.appData;
            if (p.date) this.localDate = new Date(p.date);
            if (p.startTime !== undefined && p.startTime !== null && p.startTime !== '') this.localStartTime = p.startTime;
            if (p.endTime !== undefined && p.endTime !== null && p.endTime !== '') this.localEndTime = p.endTime;
            if (p.languageIndex !== undefined && p.languageIndex !== null) this.localLanguageIndex = p.languageIndex;
            if (Array.isArray(p.selected)) this.localSelected = p.selected.slice();
            if (p.selectedItemType !== undefined && p.selectedItemType !== null && p.selectedItemType !== '') this.localSelectedItemType = p.selectedItemType;
            if (typeof p.isTalkgroup === 'boolean') this.localIsTalkgroup = p.isTalkgroup;
        }
    }
}
</script>

<style scoped>
.talk-right-pane {
    height: 100%;
    min-height: 0;
    min-width: 0;
}

.talk-right-pane-tabs {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
}

.el-tabs--border-card {
    box-shadow: none !important;
}
.el-tabs--border-card >>> .el-tabs__header .el-tabs__item:not(.is-disabled):hover {
    color: #5171cb;
    opacity: 0.7;
}
.el-tabs--border-card >>> .el-tabs__header .el-tabs__item.is-active {
    color: #848991;
    background: #f6f6f6;
    border: none;
}
.el-tabs--border-card >>> .el-tabs__header .el-tabs__item {
    color: #5171cb;
    background: #ecf1fa;
}
.el-tabs__nav-scroll {
    background: #ecf1fa;
}
.el-tabs--border-card >>> .el-tabs__header {
    border: none;
    background: #ecf1fa;
}
.el-tabs--border-card >>> .el-tabs__content {
    padding: 0px;
    display: flex;
    flex: 1;
    min-height: 0;
    min-width: 0;
}
.el-tabs--border-card >>> .el-tab-pane {
    flex: 1;
    min-height: 0;
    min-width: 0;
}
.el-tabs--border-card {
    border: none;
}

.talkgroup-user-header1 >>> .el-select .el-input {
    font-size: 12px;
    width: 200px;
}
.talkgroup-user-header1 >>> .el-input__inner {
    height: 30px;
}
.talkgroup-user-header1 >>> .el-input__icon {
    line-height: 30px;
}

</style>
